import React, { useState, useEffect, useCallback, useRef } from 'react';
import { activityLogsApi } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Phone,
  MessageSquare,
  User,
  Clock,
  RefreshCw,
  FileText,
  PhoneCall,
  PhoneMissed,
  PhoneIncoming,
  PhoneOutgoing,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

function formatTime(utcTime, timezone = 'UTC') {
  if (!utcTime) return '';
  try {
    return new Date(utcTime).toLocaleString('en-GB', {
      timeZone: timezone,
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return new Date(utcTime).toLocaleTimeString();
  }
}

function getItemIcon(item) {
  if (item.log_type === 'phone_call' || item.activity_type === 'phone_call') {
    if (item.status === 'missed') return <PhoneMissed className="w-3.5 h-3.5 text-red-500" />;
    return <PhoneCall className="w-3.5 h-3.5 text-blue-500" />;
  }
  if (item.event_type === 'response_submitted') return <MessageSquare className="w-3.5 h-3.5 text-purple-500" />;
  const type = item.log_type || item.activity_type || item.event_type || '';
  const typeMap = {
    inject_triggered: <Clock className="w-3.5 h-3.5 text-orange-500" />,
    inject_acknowledged: <Check className="w-3.5 h-3.5 text-green-500" />,
    inject_response: <MessageSquare className="w-3.5 h-3.5 text-purple-500" />,
    inject_completed: <Check className="w-3.5 h-3.5 text-green-500" />,
    message: <MessageSquare className="w-3.5 h-3.5 text-blue-500" />,
    manual_log: <FileText className="w-3.5 h-3.5 text-muted-foreground" />,
    manual: <FileText className="w-3.5 h-3.5 text-muted-foreground" />,
    note: <FileText className="w-3.5 h-3.5 text-indigo-500" />,
    system: <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />,
    system_event: <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />,
    exercise_started: <Clock className="w-3.5 h-3.5 text-green-500" />,
    exercise_paused: <Clock className="w-3.5 h-3.5 text-yellow-500" />,
    exercise_ended: <Clock className="w-3.5 h-3.5 text-red-500" />,
  };
  return typeMap[type] || <User className="w-3.5 h-3.5 text-muted-foreground" />;
}

function getItemBadge(item) {
  if (item.log_type === 'phone_call' || item.activity_type === 'phone_call') {
    if (item.status === 'missed') return { label: 'Missed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    return { label: 'Call', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  }
  if (item.event_type === 'response_submitted') {
    if (item.note_type) return { label: `Note ${item.note_type}`, cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' };
    return { label: 'Response', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' };
  }
  const map = {
    inject_triggered: { label: 'Inject', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    inject_acknowledged: { label: 'Acknowledged', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    inject_response: { label: 'Response', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    inject_completed: { label: 'Completed', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    message: { label: 'Message', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    manual_log: { label: 'Log Entry', cls: 'bg-muted text-foreground dark:bg-card dark:text-muted-foreground' },
    manual: { label: 'Log Entry', cls: 'bg-muted text-foreground dark:bg-card dark:text-muted-foreground' },
    note: { label: 'Note', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
    exercise_started: { label: 'Started', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    exercise_paused: { label: 'Paused', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    exercise_ended: { label: 'Ended', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    system_event: { label: 'System', cls: 'bg-muted text-muted-foreground dark:bg-card dark:text-muted-foreground' },
  };
  const type = item.log_type || item.activity_type || item.event_type;
  return map[type] || { label: (type || 'Event').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), cls: 'bg-muted text-muted-foreground dark:bg-card dark:text-muted-foreground' };
}

// ── Inline notes editor for call log items ──
function CallNotes({ item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await activityLogsApi.updateNotes(item.log_id, text);
      toast.success('Note saved');
      onSaved(item.log_id, text);
      setEditing(false);
    } catch (err) {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setText(item.notes || '');
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="mt-2 space-y-1.5" onClick={e => e.stopPropagation()}>
        <Textarea
          className="text-xs min-h-[60px] resize-none"
          placeholder="Add key message, observations or follow-up actions…"
          value={text}
          onChange={e => setText(e.target.value)}
          autoFocus
          data-testid="call-notes-textarea"
        />
        <div className="flex gap-1.5">
          <Button size="sm" className="h-6 text-xs px-2" onClick={handleSave} disabled={saving} data-testid="save-note-btn">
            <Check className="w-3 h-3 mr-1" />{saving ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={handleCancel}>
            <X className="w-3 h-3 mr-1" />Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      {text ? (
        <div
          className="flex items-start gap-1.5 group cursor-pointer"
          onClick={e => { e.stopPropagation(); setEditing(true); }}
          title="Click to edit note"
        >
          <span className="text-xs text-muted-foreground flex-1 italic leading-snug">"{text}"</span>
          <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0 mt-0.5" />
        </div>
      ) : (
        <button
          className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          onClick={e => { e.stopPropagation(); setEditing(true); }}
          data-testid="add-note-btn"
        >
          <Pencil className="w-3 h-3" />Add key message
        </button>
      )}
    </div>
  );
}

// Border colors matching inject log styling
const TYPE_BORDER_COLORS = {
  inject_triggered: 'border-l-orange-400',
  inject_acknowledged: 'border-l-green-400',
  inject_response: 'border-l-purple-400',
  inject_completed: 'border-l-green-500',
  response_submitted: 'border-l-purple-400',
  message: 'border-l-blue-400',
  phone_call: 'border-l-sky-400',
  manual_log: 'border-l-slate-400',
  manual: 'border-l-slate-400',
  note: 'border-l-indigo-400',
  system: 'border-l-gray-300',
  system_event: 'border-l-gray-300',
  inject_event: 'border-l-orange-400',
};

function getTypeBorder(item) {
  const type = item.event_type || item.log_type || item.activity_type || item.stream_type || '';
  return TYPE_BORDER_COLORS[type] || 'border-l-gray-300';
}

// ── Individual stream item — compact single-line layout ──
function StreamItem({ item, timezone, onNoteSaved, onItemClick }) {
  const badge = getItemBadge(item);
  const isCallItem = item.log_type === 'phone_call' || item.activity_type === 'phone_call';
  const isResponseItem = item.event_type === 'response_submitted';
  const borderCls = getTypeBorder(item);
  const msg = item.message || item.description || '';

  // V14.5 BUG 5 — make stream items clickable to jump to source
  const clickable = !!onItemClick && !!(item.inject_id || item.message_id || item.response_id || item.objective_id);
  const handleRowClick = (e) => {
    // Don't intercept clicks inside notes editor or attachments
    if (e.target.closest('button, a, input, textarea')) return;
    if (clickable) onItemClick(item);
  };

  return (
    <div
      className={`flex items-start gap-2 py-1.5 px-2 border-l-[3px] ${borderCls} border-b border-border/30 hover:bg-muted/30 transition-colors ${clickable ? 'cursor-pointer' : ''}`}
      data-testid="activity-stream-item"
      onClick={handleRowClick}
      title={clickable ? 'Click to open source' : ''}
    >
      {/* Icon */}
      <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center shrink-0 mt-px">
        {getItemIcon(item)}
      </div>

      {/* Content — compact */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          <span className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-semibold leading-none ${badge.cls}`}>
            {badge.label}
          </span>
          {isResponseItem && item.user_name && (
            <span className="text-[11px] text-muted-foreground truncate max-w-[180px]">
              {item.user_name}
            </span>
          )}
          {/* V14.10.8 — show triggering user on inject_triggered / inject_completed events */}
          {!isResponseItem && (item.event_type === 'inject_triggered' || item.event_type === 'inject_completed') && item.user_name && (
            <span className="text-[11px] text-muted-foreground truncate max-w-[200px]">
              by {item.user_name}
            </span>
          )}
          {!isResponseItem && item.from_role && (
            <span className="text-[11px] text-muted-foreground truncate max-w-[140px]">
              {item.from_role}{item.to_role ? ` \u2192 ${item.to_role}` : ''}
            </span>
          )}
        </div>
        {isResponseItem ? (
          <>
            {item.inject_title && (
              <p className="text-[11px] text-muted-foreground mt-px truncate">
                Re: {item.inject_title}
              </p>
            )}
            {item.note_text && (
              <p className="text-xs mt-px leading-tight line-clamp-2">{item.note_text}</p>
            )}
            {item.objective_met && (
              <span className="text-[10px] text-muted-foreground">Objective met: {item.objective_met}</span>
            )}
            {!!(item.attachment_ids || []).length && (
              <div className="flex gap-1 mt-0.5">
                {item.attachment_ids.map((fid) => {
                  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('ems_token') : '';
                  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${fid}?token=${encodeURIComponent(token || '')}`;
                  return (
                    <a key={fid} className="text-[10px] underline text-primary" href={fileUrl} target="_blank" rel="noreferrer">
                      Attachment
                    </a>
                  );
                })}
              </div>
            )}
            {!item.note_text && !item.inject_title && (
              <p className="text-xs mt-px leading-tight line-clamp-2">{msg}</p>
            )}
          </>
        ) : (
          <p className="text-xs mt-px leading-tight line-clamp-2">{msg}</p>
        )}
        {isCallItem && item.duration_seconds > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {Math.floor(item.duration_seconds / 60)}m {item.duration_seconds % 60}s
          </span>
        )}
        {isCallItem && item.log_id && (
          <CallNotes item={item} onSaved={(logId, text) => onNoteSaved && onNoteSaved(logId, text)} />
        )}
      </div>

      {/* Timestamp */}
      <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap mt-px">
        {formatTime(item.utc_time || item.created_at, timezone)}
      </span>
    </div>
  );
}

// ── Main component ──
export default function UnifiedActivityStream({
  exerciseId,
  timezone = 'UTC',
  refreshInterval = 10000,
  onRefresh,
  onItemClick,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadStream = useCallback(async () => {
    if (!exerciseId) return;
    try {
      const data = await activityLogsApi.getUnifiedStream(exerciseId, 100);
      setItems(data || []);
    } catch (err) {
      console.error('Failed to load activity stream:', err);
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => {
    loadStream();
    const interval = setInterval(() => {
      loadStream();
      onRefresh?.();
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [loadStream, refreshInterval, onRefresh]);

  const handleNoteSaved = (logId, text) => {
    setItems(prev => prev.map(item =>
      item.log_id === logId ? { ...item, notes: text } : item
    ));
  };

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'phone_call', label: 'Calls' },
    { value: 'inject_triggered', label: 'Injects' },
    { value: 'responses', label: 'Responses' },
    { value: 'message', label: 'Messages' },
    { value: 'logs_only', label: 'Logs' },
  ];

  const matchesFilter = (item, f) => {
    if (f === 'logs_only') {
      // Logs = manual_log items that are NOT phone calls
      return item.stream_type === 'manual_log' && item.activity_type !== 'phone_call';
    }
    if (f === 'responses') {
      // Match response_submitted events and inject_response
      return (
        item.event_type === 'response_submitted' ||
        item.log_type === 'inject_response' ||
        item.activity_type === 'inject_response' ||
        item.log_type === 'note'
      );
    }
    return (
      item.log_type === f ||
      item.activity_type === f ||
      item.event_type === f ||
      item.stream_type === f
    );
  };

  const filtered = filter === 'all'
    ? items
    : items.filter(i => matchesFilter(i, filter));

  // Height = 100% of parent (TabsContent now has explicit height)
  // Subtract filter pills height (~36px)
  const filterRef = useRef(null);
  const [filterH, setFilterH] = useState(36);

  useEffect(() => {
    if (filterRef.current) setFilterH(filterRef.current.offsetHeight);
  }, []);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Filter pills — V14.10.8: tight layout that fits the narrow side-panel
          card on production (no overflow). Falls back to horizontal scroll if
          ever cramped, but the chip sizes are tuned to fit 6 chips on ~280px. */}
      <div
        ref={filterRef}
        className="flex gap-1 flex-wrap px-1 py-1.5 border-b overflow-x-auto"
        style={{ flexShrink: 0, scrollbarWidth: 'thin' }}
      >
        {filterOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`text-[10px] leading-none px-1.5 py-1 rounded-full transition-colors whitespace-nowrap shrink-0 ${
              filter === opt.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {opt.label}
            {opt.value !== 'all' && (
              <span className="ml-1 opacity-60">
                {items.filter(i => matchesFilter(i, opt.value)).length}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={loadStream}
          className="ml-auto text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
          title="Refresh"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>

      {/* Scrollable stream — takes remaining height after filter pills */}
      <div className="overflow-y-auto px-1" style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No activity yet</p>
          </div>
        ) : (
          filtered.map((item, idx) => (
            <StreamItem
              key={item.log_id || item.event_id || idx}
              item={item}
              timezone={timezone}
              onNoteSaved={handleNoteSaved}
              onItemClick={onItemClick}
            />
          ))
        )}
      </div>
    </div>
  );
}
